import { cloneDeep } from "lodash";
import objectAssign from "object-assign";

import * as actions from "./../../types/actionTypes";
import {
  addToCart,
  removeItemFromCart,
  updateProductQuantity,
  resetCart,
} from "./../utils/cart";
import initialState from "./../initialState";

import * as cookieStorage from "./../../actions/common/cookieStorage";
import { DEFAULT_MAPS_SELECTED, DEFAULT_WINGMAN_MAPS_SELECTED } from "./../../../constants/maps";
import { isPendingRequestForRecipient, visibleIncomingFriendRequests } from "./../../../utils/friendRequests";
import { normalizeAcceptanceSlots, normalizePartyAcceptedUserIds, normalizeAcceptanceTeams } from "./../../../utils/matchAcceptance";

const cStorage = cookieStorage.getCookieStorage();
const lsKeySelectedMaps = "mapsSelected";

const handlers = new Map();

// Helper: compute sticky mapping of pending invites to empty friend slots (0..3)
const computeInviteSlotAssignments = (state) => {
  try {
    const userId = state.user && state.user.userId;
    const members = (state.currentLobby && state.currentLobby.members) || [];
    const otherMembers = members.filter((m) => m.userId !== userId).slice(0, 4);
    const indices = [0, 1, 2, 3];
    const emptySlots = indices.filter((i) => !otherMembers[i]);

    const inviteState = state.inviteState || {};
    const pendingGuestIds = Object.keys(inviteState).filter((uid) => {
      const st = inviteState[uid] || {};
      return st.invited && !st.inviteAccepted && !st.inviteError;
    });

    const prev = state.inviteSlotAssignments || {};
    const next = {};

    const stillPending = new Set(pendingGuestIds);
    Object.entries(prev).forEach(([gid, slotIndex]) => {
      if (stillPending.has(gid) && emptySlots.includes(slotIndex)) {
        next[gid] = slotIndex;
      }
    });

    const usedSlots = new Set(Object.values(next));
    const availableSlots = emptySlots.filter((s) => !usedSlots.has(s));

    for (let i = 0; i < pendingGuestIds.length; i++) {
      const gid = pendingGuestIds[i];
      if (next[gid] == null) {
        const slot = availableSlots.shift();
        if (slot == null) break;
        next[gid] = slot;
      }
    }

    return next;
  } catch (e) {
    return state.inviteSlotAssignments || {};
  }
};

handlers.set(actions.LOBBY_MAPS_UPDATE_BULK, (state, action) => {
  // Guard against undefined maps - use defaults for new users
  if (!action.maps || !Array.isArray(action.maps)) {
    console.log('[LOBBY_MAPS_UPDATE_BULK] Maps is undefined, using default maps for new user');
    return objectAssign({}, state, {
      steamData: {
        hoursPlayed: action.steamResult?.hoursPlayed || 0,
      },
      matchmakingOptions: action.matchmakingOptions || {},
      lobbyMapsSelected: DEFAULT_MAPS_SELECTED, // All maps by default for new users
      lobbyWingmanMapsSelected: Array.isArray(action.wingmanMaps)
        ? action.wingmanMaps.map((o) => o.toLowerCase().replace(/\s+/g, "_"))
        : DEFAULT_WINGMAN_MAPS_SELECTED,
    });
  }

  return objectAssign({}, state, {
    steamData: {
      hoursPlayed: action.steamResult?.hoursPlayed || 0,
    },
    matchmakingOptions: action.matchmakingOptions,
    lobbyMapsSelected: action.maps.map((o) =>
      o.toLowerCase().replace(" ", "_")
    ),
    ...(Array.isArray(action.wingmanMaps)
      ? {
          lobbyWingmanMapsSelected: action.wingmanMaps.map((o) =>
            o.toLowerCase().replace(/\s+/g, "_")
          ),
        }
      : {}),
  });
});

handlers.set(
  actions.LOBBY_PLAYER_SEARCHING_FOR_MATCH_START,
  (state, action) => {
    const newUser = objectAssign({}, state.user, {
      playerQueueActionStartTs: action.playerQueueActionStartTs,
    });
    return objectAssign({}, state, {
      lobbyState: "searching",
      user: newUser,
    });
  }
);

handlers.set(actions.LOBBY_MATCHMAKING_OPTION_CHANGED, (state, action) => {
  const { name, enabled, value, hasSlider, listValue } = action;

  let newMMOptions = objectAssign({}, state.matchmakingOptions, { [name]: enabled });
  if (hasSlider) {
    newMMOptions[`${name}_figure`] = value;
  }
  if (Array.isArray(listValue)) {
    newMMOptions[`${name}_list`] = listValue;
  }

  return objectAssign({}, state, {
    matchmakingOptions: newMMOptions,
  });
});

handlers.set(actions.LOBBY_LEADER_MATCHMAKING_OPTION_CHANGED, (state, action) => {
  const { name, enabled, value, hasSlider, listValue } = action;
  
  if (!state.currentLobby) {
    return state;
  }

  let newLobbyOptions = objectAssign({}, state.currentLobby.lobbyOptions || {}, { [name]: enabled });
  if (hasSlider) {
    newLobbyOptions[`${name}_figure`] = value;
  }
  if (Array.isArray(listValue)) {
    newLobbyOptions[`${name}_list`] = listValue;
  }

  const updatedLobby = objectAssign({}, state.currentLobby, {
    lobbyOptions: newLobbyOptions,
  });

  return objectAssign({}, state, {
    currentLobby: updatedLobby,
  });
});

handlers.set(actions.LOBBY_LEADER_UPDATED_OPTIONS, (state, action) => {
  const { lobbyCode, lobbyOptions } = action;
  
  if (!state.currentLobby || state.currentLobby.lobbyCode !== lobbyCode) {
    return state;
  }

  const updatedLobby = objectAssign({}, state.currentLobby, {
    lobbyOptions: lobbyOptions,
  });

  return objectAssign({}, state, {
    currentLobby: updatedLobby,
  });
});

handlers.set(actions.LOBBY_MATCH_FOUND_NOTIFY, (state, action) => {
  const totalPlayers =
    Number.isInteger(action.totalPlayers) && action.totalPlayers > 0
      ? action.totalPlayers
      : 2;
  const matchId =
    action.matchId != null && action.matchId !== ""
      ? String(action.matchId)
      : null;

  if (!matchId) {
    return state;
  }

  const slots = normalizeAcceptanceSlots(action.slots, totalPlayers);
  const partyAcceptedUserIds = normalizePartyAcceptedUserIds(
    action.partyAcceptedUserIds
  );
  const expiresAt = Number.isFinite(Number(action.expiresAt))
    ? Number(action.expiresAt)
    : null;
  const timeoutSeconds =
    Number.isFinite(Number(action.timeoutSeconds)) && Number(action.timeoutSeconds) > 0
      ? Number(action.timeoutSeconds)
      : expiresAt != null
        ? Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000))
        : 30;

  const nextUser = objectAssign({}, state.user);
  delete nextUser.playerQueueActionStartTs;

  return objectAssign({}, state, {
    user: nextUser,
    // Leave active queue UI: match-ready replaces searching in the lobby frame.
    lobbyState: "standby",
    matchmakingStarted: false,
    expandedSearch: false,
    queueStatus: null,
    matchAcceptDialogState: {
      open: true,
      accepted: Boolean(action.hasAccepted),
      matchId,
      timeoutSeconds,
      expiresAt,
      map: action.map || null,
      mapVotePending: Boolean(action.mapVotePending),
      location: action.location || null,
      totalPlayers,
      acceptedCount: Number.isFinite(Number(action.acceptedCount))
        ? Number(action.acceptedCount)
        : 0,
      allAccepted: Boolean(action.allAccepted),
      hasAccepted: Boolean(action.hasAccepted),
      slots,
      partyAcceptedUserIds,
      teams: normalizeAcceptanceTeams(action.teams),
    },
  });
});

handlers.set(actions.LOBBY_ACCEPT_MATCH, (state, action) => {
  if (
    !state.matchAcceptDialogState ||
    (action.matchId &&
      state.matchAcceptDialogState.matchId !== String(action.matchId))
  ) {
    return state;
  }

  return objectAssign({}, state, {
    matchAcceptDialogState: {
      ...state.matchAcceptDialogState,
      accepted: true,
      hasAccepted: true,
    },
  });
});

handlers.set(actions.UPDATE_MATCH_ACCEPTANCE_STATUS, (state, action) => {
  // Only update if we have an open match dialog for this match
  if (state.matchAcceptDialogState && state.matchAcceptDialogState.matchId === action.matchId) {
    const totalPlayers =
      Number.isInteger(action.totalPlayers) && action.totalPlayers > 0
        ? action.totalPlayers
        : state.matchAcceptDialogState.totalPlayers;
    const slots =
      action.slots != null
        ? normalizeAcceptanceSlots(action.slots, totalPlayers)
        : state.matchAcceptDialogState.slots;
    const partyAcceptedUserIds =
      action.partyAcceptedUserIds != null
        ? normalizePartyAcceptedUserIds(action.partyAcceptedUserIds)
        : state.matchAcceptDialogState.partyAcceptedUserIds || [];

    return objectAssign({}, state, {
      matchAcceptDialogState: {
        ...state.matchAcceptDialogState,
        acceptedCount: action.acceptedCount,
        totalPlayers,
        allAccepted: action.allAccepted,
        hasAccepted:
          action.hasAccepted != null
            ? Boolean(action.hasAccepted)
            : state.matchAcceptDialogState.hasAccepted,
        expiresAt:
          action.expiresAt != null
            ? Number(action.expiresAt)
            : state.matchAcceptDialogState.expiresAt,
        accepted:
          action.hasAccepted != null
            ? Boolean(action.hasAccepted) || state.matchAcceptDialogState.accepted
            : state.matchAcceptDialogState.accepted,
        slots,
        partyAcceptedUserIds,
        mapVotePending:
          action.mapVotePending != null
            ? Boolean(action.mapVotePending)
            : state.matchAcceptDialogState.mapVotePending,
        map:
          action.map !== undefined
            ? action.map || null
            : state.matchAcceptDialogState.map,
        teams:
          action.teams != null
            ? normalizeAcceptanceTeams(action.teams)
            : state.matchAcceptDialogState.teams ||
              normalizeAcceptanceTeams(null),
      },
    });
  }
  return state;
});

handlers.set(actions.MATCH_ACCEPTANCE_CANCELLED, (state, action) => {
  const activeMatchId = state.matchAcceptDialogState?.matchId;
  const cancelledMatchId =
    action.matchId != null && action.matchId !== ""
      ? String(action.matchId)
      : null;

  if (
    cancelledMatchId &&
    activeMatchId &&
    cancelledMatchId !== String(activeMatchId)
  ) {
    return state;
  }

  const outcome = action.outcome === "requeued" ? "requeued" : "returned-to-lobby";
  const nextUser = objectAssign({}, state.user);
  delete nextUser.playerQueueActionStartTs;

  let restoredStartTs = Number(action.playerQueueActionStartTs);
  // Defensive: server uses unix seconds; reject accidental millisecond values.
  if (Number.isFinite(restoredStartTs) && restoredStartTs > 1e12) {
    restoredStartTs = Math.floor(restoredStartTs / 1000);
  }

  if (
    outcome === "requeued" &&
    Number.isFinite(restoredStartTs) &&
    restoredStartTs > 0
  ) {
    nextUser.playerQueueActionStartTs = restoredStartTs;
    return objectAssign({}, state, {
      user: nextUser,
      matchAcceptDialogState: false,
      // Cancelled matches must not leave a stale vote modal after requeue.
      mapVoteState: false,
      matchFound: null,
      lobbyState: "searching",
      matchmakingStarted: true,
      expandedSearch: false,
    });
  }

  return objectAssign({}, state, {
    user: nextUser,
    matchAcceptDialogState: false,
    mapVoteState: false,
    matchFound: null,
    lobbyState: "standby",
    matchmakingStarted: false,
    expandedSearch: false,
  });
});

handlers.set(actions.START_MAP_VOTE, (state, action) => {
  const matchId =
    action.matchId != null && action.matchId !== ""
      ? String(action.matchId)
      : null;

  if (!matchId) {
    return state;
  }

  return objectAssign({}, state, {
    // Vote replaces accept; leaving the accept dialog open would stack both modals.
    matchAcceptDialogState: false,
    mapVoteState: {
      matchId,
      candidates: Array.isArray(action.candidates) ? action.candidates : [],
      blockedMaps: Array.isArray(action.blockedMaps) ? action.blockedMaps : [],
      voters: Array.isArray(action.voters) ? action.voters : [],
      votes: action.votes && typeof action.votes === "object" ? action.votes : {},
      myVote: action.myVote || null,
      expiresAt: Number.isFinite(Number(action.expiresAt))
        ? Number(action.expiresAt)
        : null,
      durationSeconds:
        Number(action.durationSeconds) > 0 ? Number(action.durationSeconds) : 30,
      resolvedMap: null,
      tally: null,
      wasTie: false,
      tieBrokenBy: null,
      tiedMaps: [],
      resolvedBy: null,
      topCount: null,
      teamStats:
        action.teamStats && typeof action.teamStats === "object"
          ? action.teamStats
          : null,
    },
  });
});

handlers.set(actions.UPDATE_MAP_VOTE, (state, action) => {
  if (
    !state.mapVoteState ||
    state.mapVoteState.matchId !== String(action.matchId)
  ) {
    return state;
  }

  let expiresAt = state.mapVoteState.expiresAt;
  if (action.expiresAt != null) {
    const nextExpiresAt = Number(action.expiresAt);
    if (Number.isFinite(nextExpiresAt)) {
      expiresAt = nextExpiresAt;
    }
  }

  return objectAssign({}, state, {
    mapVoteState: objectAssign({}, state.mapVoteState, {
      votes:
        action.votes && typeof action.votes === "object"
          ? action.votes
          : state.mapVoteState.votes,
      voters: Array.isArray(action.voters)
        ? action.voters
        : state.mapVoteState.voters,
      candidates: Array.isArray(action.candidates)
        ? action.candidates
        : state.mapVoteState.candidates,
      blockedMaps: Array.isArray(action.blockedMaps)
        ? action.blockedMaps
        : state.mapVoteState.blockedMaps,
      myVote:
        action.myVote !== undefined ? action.myVote : state.mapVoteState.myVote,
      expiresAt,
      teamStats:
        action.teamStats && typeof action.teamStats === "object"
          ? action.teamStats
          : state.mapVoteState.teamStats,
    }),
  });
});

handlers.set(actions.CAST_MAP_VOTE_LOCALLY, (state, action) => {
  if (
    !state.mapVoteState ||
    state.mapVoteState.matchId !== String(action.matchId)
  ) {
    return state;
  }

  return objectAssign({}, state, {
    // Shared votes stay server-owned; this only paints the viewer's own pick immediately.
    mapVoteState: objectAssign({}, state.mapVoteState, {
      myVote: action.map,
    }),
  });
});

handlers.set(actions.RESOLVE_MAP_VOTE, (state, action) => {
  if (
    !state.mapVoteState ||
    state.mapVoteState.matchId !== String(action.matchId)
  ) {
    return state;
  }

  return objectAssign({}, state, {
    mapVoteState: objectAssign({}, state.mapVoteState, {
      votes:
        action.votes && typeof action.votes === "object"
          ? action.votes
          : state.mapVoteState.votes,
      voters: Array.isArray(action.voters)
        ? action.voters
        : state.mapVoteState.voters,
      resolvedMap: action.map || null,
      tally: action.tally || {},
      wasTie: Boolean(action.wasTie),
      tieBrokenBy: action.tieBrokenBy || null,
      tiedMaps: Array.isArray(action.tiedMaps) ? action.tiedMaps : [],
      resolvedBy: action.resolvedBy || null,
      topCount: Number.isFinite(Number(action.topCount))
        ? Number(action.topCount)
        : null,
      teamStats:
        action.teamStats && typeof action.teamStats === "object"
          ? action.teamStats
          : state.mapVoteState.teamStats,
    }),
  });
});

handlers.set(actions.CLOSE_MAP_VOTE, (state) => {
  return objectAssign({}, state, {
    mapVoteState: false,
  });
});

handlers.set(actions.LOBBY_PLAYER_STOP_SEARCH, (state) => {
  const newUser = objectAssign({}, state.user);
  delete newUser.playerQueueActionStartTs;

  return objectAssign({}, state, {
    user: newUser,
    lobbyState: "standby",
    expandedSearch: false,
    matchmakingStarted: false,
  });
});

handlers.set(actions.LOAD_USER_PROFILE, (state, action) => {
  const incomingUser = action.user || {};
  const nextUser = objectAssign({}, incomingUser);
  const startTs = Number(nextUser.playerQueueActionStartTs);
  const isActivelyQueueing =
    nextUser.playerStatus === "inMatchMaking" &&
    Number.isFinite(startTs) &&
    startTs > 0;

  // Stale playerQueueActionStartTs without inMatchMaking must not resurrect
  // searching UI after cancel + refresh.
  if (!isActivelyQueueing && "playerQueueActionStartTs" in nextUser) {
    delete nextUser.playerQueueActionStartTs;
  }

  const lobbyStateExt = {};
  if (isActivelyQueueing) {
    lobbyStateExt.lobbyState = "searching";
  } else if (state.lobbyState === "searching" && !isActivelyQueueing) {
    // Profile refresh after cancel: drop ghost searching state.
    lobbyStateExt.lobbyState = "standby";
    lobbyStateExt.matchmakingStarted = false;
  }

  return objectAssign({}, state, {
    user: nextUser,
    account: action.clearAccountDraft ? {} : state.account,
    performLogout: false,
    ...lobbyStateExt,
  });
});

function normalizeReducerMapKey(value) {
  return String(value || "").toLowerCase().replace(/\s+/g, "_");
}

function toggleMapKey(currentMaps, mapName) {
  const normalizedMapName = normalizeReducerMapKey(mapName);
  const newMapList = Array.isArray(currentMaps) ? [...currentMaps] : [];
  const mapIndex = newMapList.findIndex((o) => o === normalizedMapName);
  if (mapIndex !== -1) {
    newMapList.splice(mapIndex, 1);
  } else if (normalizedMapName) {
    newMapList.push(normalizedMapName);
  }
  return newMapList;
}

handlers.set(actions.LOBBY_MAP_PICK, (state, action) => {
  const isWingman = action.queueMode === "wingman";
  const field = isWingman ? "lobbyWingmanMapsSelected" : "lobbyMapsSelected";
  return objectAssign({}, state, {
    [field]: toggleMapKey(state[field], action.name),
  });
});

handlers.set(actions.LOBBY_MAPS_TOGGLE_ALL, (state, action) => {
  const isWingman = action.queueMode === "wingman";
  const field = isWingman ? "lobbyWingmanMapsSelected" : "lobbyMapsSelected";
  return objectAssign({}, state, {
    [field]: (action.maps || []).map((o) => normalizeReducerMapKey(o)),
  });
});

handlers.set(actions.LOBBY_LEADER_MAP_TOGGLED, (state, action) => {
  if (!state.currentLobby) {
    return state;
  }

  const isWingman = state.currentLobby.queueMode === "wingman";
  const field = isWingman ? "lobbyWingmanMapsSelected" : "lobbyMapsSelected";
  const currentMaps = Array.isArray(state.currentLobby[field])
    ? state.currentLobby[field]
    : (action.fallbackMaps || []).map((mapName) => normalizeReducerMapKey(mapName));

  const updatedLobby = objectAssign({}, state.currentLobby, {
    [field]: toggleMapKey(currentMaps, action.mapName),
  });

  return objectAssign({}, state, {
    currentLobby: updatedLobby,
  });
});

handlers.set(actions.LOBBY_LEADER_MAPS_TOGGLE_ALL, (state, action) => {
  if (!state.currentLobby) {
    return state;
  }

  const isWingman = state.currentLobby.queueMode === "wingman";
  const field = isWingman ? "lobbyWingmanMapsSelected" : "lobbyMapsSelected";
  const newMapList = action.selectAll
    ? action.maps.map((o) => normalizeReducerMapKey(o))
    : [];

  const updatedLobby = objectAssign({}, state.currentLobby, {
    [field]: newMapList,
  });

  return objectAssign({}, state, {
    currentLobby: updatedLobby,
  });
});

handlers.set(actions.LOBBY_LEADER_UPDATED_MAPS, (state, action) => {
  if (!state.currentLobby || state.currentLobby.lobbyCode !== action.lobbyCode) {
    return state;
  }

  const patch = {};
  if (Array.isArray(action.lobbyMapsSelected)) {
    patch.lobbyMapsSelected = action.lobbyMapsSelected;
  }
  if (Array.isArray(action.lobbyWingmanMapsSelected)) {
    patch.lobbyWingmanMapsSelected = action.lobbyWingmanMapsSelected;
  }
  if (Object.keys(patch).length === 0) {
    return state;
  }

  return objectAssign({}, state, {
    currentLobby: objectAssign({}, state.currentLobby, patch),
  });
});

function applyLobbyQueueMode(state, action) {
  if (!state.currentLobby) {
    return state;
  }

  const updatedLobby = objectAssign({}, state.currentLobby, {
    queueMode: action.queueMode || "standard",
    maxPlayers: action.maxPlayers || (action.queueMode === "wingman" ? 2 : 5),
    ...(Array.isArray(action.lobbyWingmanMapsSelected)
      ? { lobbyWingmanMapsSelected: action.lobbyWingmanMapsSelected }
      : {}),
  });

  return objectAssign({}, state, {
    currentLobby: updatedLobby,
  });
}

handlers.set(actions.LOBBY_QUEUE_MODE_SWITCHED, applyLobbyQueueMode);
handlers.set(actions.LOBBY_QUEUE_MODE_UPDATED, (state, action) => {
  if (state.currentLobby && action.lobbyCode && state.currentLobby.lobbyCode !== action.lobbyCode) {
    return state;
  }
  return applyLobbyQueueMode(state, action);
});

handlers.set(actions.LOBBY_SERVER_LOCATION_TOGGLED, (state, action) => {
  const currentLocations = Array.isArray(state.selectedServerLocations)
    ? state.selectedServerLocations
    : [];
  const nextLocations = currentLocations.includes(action.locationId)
    ? currentLocations.filter((locationId) => locationId !== action.locationId)
    : [...currentLocations, action.locationId];

  return objectAssign({}, state, {
    selectedServerLocations: nextLocations,
  });
});

handlers.set(actions.LOBBY_LEADER_SERVER_LOCATION_TOGGLED, (state, action) => {
  if (!state.currentLobby) {
    return state;
  }

  const currentLocations =
    Array.isArray(state.currentLobby.lobbyServerLocationsSelected)
      ? state.currentLobby.lobbyServerLocationsSelected
      : Array.isArray(action.fallbackLocations)
        ? action.fallbackLocations
        : [];
  const nextLocations = currentLocations.includes(action.locationId)
    ? currentLocations.filter((locationId) => locationId !== action.locationId)
    : [...currentLocations, action.locationId];

  return objectAssign({}, state, {
    currentLobby: objectAssign({}, state.currentLobby, {
      lobbyServerLocationsSelected: nextLocations,
    }),
  });
});

handlers.set(actions.LOBBY_LEADER_UPDATED_SERVER_LOCATIONS, (state, action) => {
  if (!state.currentLobby || state.currentLobby.lobbyCode !== action.lobbyCode) {
    return state;
  }

  return objectAssign({}, state, {
    currentLobby: objectAssign({}, state.currentLobby, {
      lobbyServerLocationsSelected: action.lobbyServerLocationsSelected,
    }),
  });
});

handlers.set(actions.PERFORM_LOGOUT, (state, action) => {
  return objectAssign({}, state, {
    avatarCacheBust: 0,
    user: null,
    performLogout: true,
    // Clear all user-specific data during logout
    friends: [],
    friendRequests: [],
    pendingRequests: [],
    friendRequestSuccess: null,
    friendRequestError: null,
    deleteDialogOpen: false,
    friendToDelete: null,
    inviteData: null,
    inviteDialogOpen: false,
    inviteState: {},
    inviteSlotAssignments: {},
    confirmDialog: { open: false, which: null },
    platformStats: { serversOnline: null, playersSearching: null, activeMatches: null },
  });
});

handlers.set(actions.UPDATE_DISPLAY_PRESENCE, (state, action) => {
  if (!state.user || !action.displayPresence) return state;
  return objectAssign({}, state, {
    user: objectAssign({}, state.user, {
      displayPresence: action.displayPresence,
    }),
  });
});

handlers.set(actions.SET_REQUEST_DEVICE, (state, action) => {
  return objectAssign({}, state, {
    requestDevice: {
      isAndroidOrIosPhone: action.isAndroidOrIosPhone === true,
    },
  });
});

handlers.set(actions.SET_PUBLIC_TEMPLATE, (state, action) => {
  return objectAssign({}, state, {
    publicTemplate: {
      codename: action.codename || "original",
      isResolved: action.isResolved === true,
    },
  });
});

handlers.set(actions.LOADING, (state, action) => {
  return objectAssign({}, state, {
    listPending: action.loading,
  });
});

handlers.set(actions.ACCOUNT_SUCCESS_ALERT_SNOOZE, (state) => {
  return objectAssign({}, state, {
    accountUpdateComplete: false,
  });
});

handlers.set(actions.ACCOUNT_UPDATE_ALERT, (state, action) => {
  return objectAssign({}, state, {
    accountUpdateDuring: false,
    accountUpdateComplete: false,
    accountUpdateAlert: true,
    accountUpdateAlertText: action.text,
  });
});

handlers.set(actions.ACCOUNT_UPDATE_ALERT_SNOOZE, (state) => {
  return objectAssign({}, state, {
    accountUpdateAlert: false,
    accountUpdateAlertText: "",
  });
});

handlers.set(actions.ACCOUNT_UPDATE_COMPLETE, (state, action) => {
  const responsePayload = action.response?.result || action.response || {};
  const authoritativeUser =
    responsePayload?.authoritativeUser &&
    typeof responsePayload.authoritativeUser === "object"
      ? responsePayload.authoritativeUser
      : null;
  const refreshedUser =
    action.response?.refreshedUser &&
    typeof action.response.refreshedUser === "object"
      ? action.response.refreshedUser
      : null;
  const responseAvatar =
    authoritativeUser?.avatar ||
    responsePayload?.avatar ||
    action.response?.avatar ||
    null;
  const responsePicture =
    authoritativeUser?.picture ||
    responsePayload?.picture ||
    action.response?.picture ||
    null;
  const serverAvatarUpdatedAtMs =
    typeof authoritativeUser?.avatarUpdatedAtMs === "number"
      ? authoritativeUser.avatarUpdatedAtMs
      : typeof authoritativeUser?.avatar?.updatedAtMs === "number"
        ? authoritativeUser.avatar.updatedAtMs
        : typeof action.response?.avatarUpdatedAtMs === "number"
          ? action.response.avatarUpdatedAtMs
          : typeof responsePayload?.avatarUpdatedAtMs === "number"
            ? responsePayload.avatarUpdatedAtMs
            : typeof action.response?.avatar?.updatedAtMs === "number"
              ? action.response.avatar.updatedAtMs
              : typeof responsePayload?.avatar?.updatedAtMs === "number"
                ? responsePayload.avatar.updatedAtMs
                : null;
  const newUser = objectAssign(
    {},
    state.user,
    state.account,
    refreshedUser || {},
    authoritativeUser || {}
  );

  if (responsePicture) {
    newUser.picture = responsePicture;
  }

  if (typeof serverAvatarUpdatedAtMs === "number") {
    newUser.avatarUpdatedAtMs = serverAvatarUpdatedAtMs;
  }

  if (responseAvatar && typeof responseAvatar === "object") {
    newUser.avatar = responseAvatar;
  } else if (typeof serverAvatarUpdatedAtMs === "number") {
    newUser.avatar = objectAssign({}, state.user?.avatar, {
      updatedAtMs: serverAvatarUpdatedAtMs,
    });
  }

  return objectAssign({}, state, {
    account: {},
    user: newUser,
    accountUpdateAlert: false,
    accountUpdateAlertText: "",
    accountUpdateComplete: true,
    accountUpdateDuring: false,
    accountUpdateCompleteResult: action.response,
    avatarCacheBust:
      typeof serverAvatarUpdatedAtMs === "number"
        ? serverAvatarUpdatedAtMs
        : state.avatarCacheBust,
  });
});

handlers.set(actions.ACCOUNT_UPDATE_DURING, (state) => {
  return objectAssign({}, state, {
    accountUpdateDuring: true,
  });
});

handlers.set(actions.ACCOUNT_FORM_FIELD_CHANGE, (state, { name, value }) => {
  let aValue = value;
  if (name === "age") {
    aValue = value.replace(/[^0-9]/g, "");
    if (parseInt(aValue) > 90) {
      aValue = 90;
    }
  }
  if (name === "dateOfBirth") {
    aValue = typeof value === "string" ? value : "";
  }

  const newAccount = objectAssign({}, state.account, {
    [name]: aValue,
  });

  return objectAssign({}, state, {
    account: newAccount,
  });
});

handlers.set(actions.SET_PROFILE_DATA_LOADING, (state, action) => {
  return objectAssign({}, state, {
    profileDataLoading: action.loading,
  });
});

handlers.set(actions.GET_PROFILE_PAGE_USER_DATA, (state, action) => {
  return objectAssign({}, state, {
    profileData: action.profileData,
    profileDataLoading: false,
    matchSummary:  action.matchSummary,
    mapsStats: action.mapsStats,
    last25MatchData: action.last25MatchData,
    recentStats: action.recentStats,
    activityMatches: action.activityMatches || [],
  });
});

handlers.set(actions.SORT_OPTION_MATCH_TABLE, (state, action) => {
  const { sortType, value } = action.payload;

  return objectAssign({}, state, {
    sortOptions: {
      ...state.sortOptions,
      [sortType]: value,
    },
  });
});

handlers.set(actions.GET_MATCH_DATA, (state, action) => {
  return objectAssign({}, state, {
    matchData: action.matchData,
    scoreboardData: action.scoreboardData,
    matchAnalysis: action.matchAnalysis,
    usersMetaData: action.usersMetaData,
    matchFound: action.matchFound,
    matchPageMissingId:
      action.matchPageMissingId === undefined
        ? state.matchPageMissingId
        : action.matchPageMissingId,
  });
});

handlers.set(actions.GET_SCOREBOARD_DATA, (state, action) => {
  return objectAssign({}, state, {
    scoreboardData: action.scoreboardData,
  });
})

handlers.set(actions.LOBBY_SEND_FRIEND_REQUEST_ERROR, (state, action) => {
  return objectAssign({}, state, {
    friendRequestError: action.friendRequestError,
    friendRequestSuccess: null,
  });
});

handlers.set(actions.LOBBY_SEND_FRIEND_REQUEST_SUCCESS, (state, action) => {
  return objectAssign({}, state, {
    friendRequestSuccess: action.friendRequestSuccess,
    friendRequestError: null,
  });
});

handlers.set(actions.LOBBY_CLEAR_FRIEND_REQUEST_MESSAGES, (state, action) => {
  return objectAssign({}, state, {
    friendRequestSuccess: null,
    friendRequestError: null,
  });
});

handlers.set(actions.GET_PROFILE_RELATIONSHIP, (state, action) => {
  return objectAssign({}, state, {
    profileRelationship: action.profileRelationship,
    profileRelationshipContext: {
      viewerSteamId: action.viewerSteamId,
      targetSteamId: action.targetSteamId,
    },
  });
});

handlers.set(actions.LOBBY_GET_FRIEND_REQUESTS, (state, action) => {
  return objectAssign({}, state, {
    friendRequests: visibleIncomingFriendRequests(action.friendRequests),
  });
});

handlers.set(actions.LOBBY_GET_PENDING_REQUESTS, (state, action) => {
  return objectAssign({}, state, {
    pendingRequests: action.pendingRequests,
  });
});

handlers.set(actions.ADD_PENDING_REQUEST_LOCALLY, (state, action) => {
  const existingPendingRequests = state.pendingRequests || [];
  const newPendingRequests = [...existingPendingRequests, action.pendingRequest];
  
  return objectAssign({}, state, {
    pendingRequests: newPendingRequests,
  });
});

handlers.set(actions.REMOVE_PENDING_REQUEST_LOCALLY, (state, action) => {
  const existingPendingRequests = state.pendingRequests || [];
  
  const newPendingRequests = existingPendingRequests.filter(req => {
    return !isPendingRequestForRecipient(req, action.recipientSteamId);
  });
  
  return objectAssign({}, state, {
    pendingRequests: newPendingRequests,
  });
});

handlers.set(actions.ADD_FRIEND_REQUEST_LOCALLY, (state, action) => {
  const existingFriendRequests = state.friendRequests || [];
  const newFriendRequests = visibleIncomingFriendRequests([
    ...existingFriendRequests,
    action.friendRequest,
  ]);
  
  return objectAssign({}, state, {
    friendRequests: newFriendRequests,
  });
});

handlers.set(actions.REMOVE_FRIEND_LOCALLY, (state, action) => {
  const existingFriends = state.friends || [];
  const newFriends = existingFriends.filter(
    friend => String(friend.friendSteamId) !== String(action.friendSteamId)
  );
  
  return objectAssign({}, state, {
    friends: newFriends,
  });
});

handlers.set(actions.LOBBY_ACCEPT_FRIEND_REQUEST, (state, action) => {
  return objectAssign({}, state, {
    friendRequestAccepted: action.friendRequestAccepted,
  });
});

handlers.set(actions.LOBBY_DECLINE_FRIEND_REQUEST, (state, action) => {
  return objectAssign({}, state, {
    friendRequestDeclined: action.friendRequestDeclined,
  });
});

handlers.set(actions.LOBBY_CANCEL_FRIEND_REQUEST, (state, action) => {
  return objectAssign({}, state, {
    friendRequests: state.friendRequests.filter(request => request._id !== action.friendRequestId),
  });
});


handlers.set(actions.LOBBY_GET_FRIENDS, (state, action) => {
  return objectAssign({}, state, {
    friends: action.friends,
  });
});

handlers.set(actions.LOBBY_UPDATE_FRIEND_STATUS, (state, action) => {
  const targetSteamId = String(action.friendSteamId ?? "");
  if (!targetSteamId || !Array.isArray(state.friends)) return state;

  let changed = false;
  const friends = state.friends.map((friend) => {
    if (String(friend?.friendSteamId ?? "") !== targetSteamId) return friend;

    changed = true;
    return {
      ...friend,
      friendData: {
        ...(friend.friendData || {}),
        isOnline: action.isOnline,
        playerStatus: action.playerStatus,
        lastOnlineUpdate: action.lastOnlineUpdate,
      },
    };
  });

  return changed ? { ...state, friends } : state;
});

handlers.set(actions.LOBBY_UPDATE_MEMBER_STATUS, (state, action) => {
  if (!state.currentLobby || !Array.isArray(state.currentLobby.members)) {
    return state;
  }

  const targetUserId = String(action.userId ?? "");
  if (!targetUserId) return state;

  let changed = false;
  const members = state.currentLobby.members.map((member) => {
    if (String(member?.userId ?? "") !== targetUserId) return member;

    changed = true;
    return {
      ...member,
      isOnline: action.isOnline,
      playerStatus: action.playerStatus,
      lastOnlineUpdate: action.lastOnlineUpdate,
    };
  });

  if (!changed) return state;

  return {
    ...state,
    currentLobby: {
      ...state.currentLobby,
      members,
    },
  };
});

handlers.set(actions.LOBBY_SEARCH_USERS_LOADING, (state, action) => {
  return objectAssign({}, state, {
    playerSearch: {
      ...state.playerSearch,
      loading: true,
      offset: action.offset ?? 0,
      error: null,
      requestId: action.requestId ?? null,
    },
  });
});

handlers.set(actions.LOBBY_SEARCH_USERS_RESULTS, (state, action) => {
  if (action.requestId && action.requestId !== state.playerSearch?.requestId) {
    return state;
  }
  const shouldAppend = action.append === true;
  let nextUsers = state.friendSearchResults;

  if (Array.isArray(action.users)) {
    if (shouldAppend) {
      const seen = new Set();
      nextUsers = [];
      for (const user of [...state.friendSearchResults, ...action.users]) {
        const key = user?._id || user?.steamId;
        if (!key || seen.has(key)) {
          continue;
        }
        seen.add(key);
        nextUsers.push(user);
      }
    } else {
      nextUsers = action.users;
    }
  }

  return objectAssign({}, state, {
    friendSearchResults: nextUsers,
    playerSearch: {
      ...state.playerSearch,
      loading: false,
      total: action.total ?? state.playerSearch?.total ?? 0,
      offset: action.offset ?? state.playerSearch?.offset ?? 0,
      error: action.error || null,
      requestId: action.requestId ?? state.playerSearch?.requestId ?? null,
    },
  });
});

handlers.set(actions.LOBBY_CLEAR_SEARCH_USERS, (state) => {
  return objectAssign({}, state, {
    friendSearchResults: [],
    playerSearch: {
      loading: false,
      total: 0,
      offset: 0,
      error: null,
      requestId: null,
    },
  });
});

handlers.set(actions.GLOBAL_PLAYER_SEARCH_LOADING, (state, action) => {
  return objectAssign({}, state, {
    globalPlayerSearch: {
      ...state.globalPlayerSearch,
      loading: true,
      error: null,
      requestId: action.requestId ?? null,
    },
  });
});

handlers.set(actions.GLOBAL_PLAYER_SEARCH_RESULTS, (state, action) => {
  if (action.requestId && action.requestId !== state.globalPlayerSearch?.requestId) {
    return state;
  }
  return objectAssign({}, state, {
    globalPlayerSearchResults: Array.isArray(action.users)
      ? action.users
      : state.globalPlayerSearchResults,
    globalPlayerSearch: {
      ...state.globalPlayerSearch,
      loading: false,
      error: action.error || null,
      requestId: action.requestId ?? state.globalPlayerSearch?.requestId ?? null,
    },
  });
});

handlers.set(actions.GLOBAL_PLAYER_SEARCH_CLEAR, (state) => {
  return objectAssign({}, state, {
    globalPlayerSearchResults: [],
    globalPlayerSearch: { loading: false, error: null, requestId: null },
  });
});

handlers.set(actions.LOBBY_OPEN_CONFIRM_DIALOG, (state, action) => {
  return {
    ...state,
    confirmDialog: {
      open: true,
      which: action.which,
    },
    deleteDialogOpen: action.which === 'deleteFriend',
    friendToDelete: action.which === 'deleteFriend' ? action.friend : state.friendToDelete,
  };
});

handlers.set(actions.LOBBY_CLOSE_CONFIRM_DIALOG, (state, action) => {
  return {
    ...state,
    confirmDialog: { open: false, which: null },
    deleteDialogOpen: false,
    friendToDelete: null,
  };
});

handlers.set(actions.LOBBY_OPEN_JOIN_LOBBY_DIALOG, (state, action) => {
  return {
    ...state,
    joinLobbyDialogOpened: true,
  };
});

handlers.set(actions.LOBBY_CLOSE_JOIN_LOBBY_DIALOG, (state, action) => {
  return {
    ...state,
    joinLobbyDialogOpened: false,
  };
});

handlers.set(actions.LOBBY_FRIEND_REQUEST_ACCEPTED, (state, action) => {
  const updatedFriends = [...state.friends, action.newFriend];
  
  const updatedFriendRequests = state.friendRequests.filter(
    request => request.fromUser.steamId !== action.newFriend.friendSteamId
  );
  
  return {
    ...state,
    friends: updatedFriends,
    friendRequests: updatedFriendRequests,
  };
});

handlers.set(actions.LOBBY_INVITE_START, (state, action) => {
  return {
    ...state,
    inviteData: action.inviteData,
    inviteCountdown: action.inviteData.expiresIn,
    inviteDialogOpen: true,
    inviteError: null,
  };
});

handlers.set(actions.LOBBY_INVITE_SENT, (state, action) => {
  const updated = {
    ...state,
    inviteState: {
      ...state.inviteState,
      [action.guestId]: {
        invited: true,
        inviteError: null,
      },
    },
  };
  return {
    ...updated,
    inviteSlotAssignments: computeInviteSlotAssignments(updated),
  };
});

handlers.set(actions.LOBBY_INVITE_EXPIRED, (state, action) => {
  const updated = {
    ...state,
    inviteState: {
      ...state.inviteState,
      [action.guestId]: {
        invited: true,
        inviteError: "Invite expired",
        inviteAccepted: false,
      },
    },
    inviteDialogOpen: false,
    inviteData: null,
    inviteCountdown: 0,
  };
  return {
    ...updated,
    inviteSlotAssignments: computeInviteSlotAssignments(updated),
  };
});

handlers.set(actions.LOBBY_INVITE_DECLINED, (state, action) => {
  const updated = {
    ...state,
    inviteState: {
      ...state.inviteState,
      [action.guestId]: {
        invited: false,
        inviteError: "Invite declined",
        inviteAccepted: false,
      },
    },
    inviteDialogOpen: false,
    inviteData: null,
    inviteCountdown: 0,
  };
  return {
    ...updated,
    inviteSlotAssignments: computeInviteSlotAssignments(updated),
  };
});

handlers.set(actions.LOBBY_INVITE_ACCEPTED, (state, action) => {
  const updated = {
    ...state,
    inviteState: {
      ...state.inviteState,
      [action.guestId]: {
        invited: true,
        inviteError: null,
        inviteAccepted: true,
      },
    },
    inviteDialogOpen: false,
    inviteData: null,
    inviteCountdown: 0,
  };
  return {
    ...updated,
    inviteSlotAssignments: computeInviteSlotAssignments(updated),
  };
});

handlers.set(actions.LOBBY_INVITE_CANCELLED, (state, action) => {
  const nextInviteState = { ...(state.inviteState || {}) };
  if (action.guestId && nextInviteState[action.guestId]) {
    delete nextInviteState[action.guestId];
  }

  const incoming = state.inviteData;
  const matchesOpenInvite = Boolean(
    incoming &&
      ((action.inviteId &&
        String(incoming.inviteId) === String(action.inviteId)) ||
        (action.fromUserId &&
          String(incoming.fromUserId) === String(action.fromUserId)))
  );

  const updated = {
    ...state,
    inviteState: nextInviteState,
    inviteDialogOpen: matchesOpenInvite ? false : state.inviteDialogOpen,
    inviteData: matchesOpenInvite ? null : state.inviteData,
    inviteCountdown: matchesOpenInvite ? 0 : state.inviteCountdown,
  };
  return {
    ...updated,
    inviteSlotAssignments: computeInviteSlotAssignments(updated),
  };
});

handlers.set(actions.LOBBY_CREATE_NEW_LOBBY, (state, action) => {
  return {
    ...state,
    currentLobbyCode: action.lobby.lobbyCode || null,   
    currentLobby: action.lobby,                 
  };
});

handlers.set(actions.LOBBY_PLAYER_LEFT, (state, action) => {
  const currentLobby = state.currentLobby;
  if (!currentLobby || currentLobby.lobbyCode !== action.lobbyCode) {
    return state;
  }

  const filteredMembers = (currentLobby.members || []).filter(m => m.userId !== action.leftUserId);

  let newHostId = currentLobby.hostId;
  let membersWithRole = filteredMembers;

  if (action.hostChanged && action.newHostId) {
    newHostId = action.newHostId;
    membersWithRole = filteredMembers.map(m =>
      m.userId === action.newHostId ? { ...m, role: "host" } : m
    );
  }

  const updatedLobby = {
    ...currentLobby,
    members: membersWithRole,
    hostId: newHostId,
    totalPlayers:
      typeof action.totalPlayers === "number"
        ? action.totalPlayers
        : Math.max(0, (currentLobby.totalPlayers || membersWithRole.length + 1) - 1),
    lastUpdatedAt: new Date().toISOString(),
  };

  const updatedState = {
    ...state,
    currentLobby: updatedLobby,
  };
  // Clear invite state for the user who left to ensure FriendRow stops showing "In Your Team"
  const nextInviteState = { ...(state.inviteState || {}) };
  if (action.leftUserId && nextInviteState[action.leftUserId]) {
    delete nextInviteState[action.leftUserId];
  }

  return {
    ...updatedState,
    inviteSlotAssignments: computeInviteSlotAssignments(updatedState),
    inviteState: nextInviteState,
  };
});

handlers.set(actions.LOBBY_UPDATE_ADDED_NEW_PLAYER, (state, action) => {
  const updatedState = {
    ...state,
    currentLobbyCode: action.lobby.lobbyCode || null,
    currentLobby: action.lobby,
  };
  return {
    ...updatedState,
    inviteSlotAssignments: computeInviteSlotAssignments(updatedState),
  };
});

handlers.set(actions.LOBBY_LEAVE_LOBBY, (state, action) => {
  const nextUser = state.user ? objectAssign({}, state.user) : state.user;
  if (nextUser && "playerQueueActionStartTs" in nextUser) {
    delete nextUser.playerQueueActionStartTs;
  }

  return {
    ...state,
    user: nextUser,
    currentLobbyCode: null,
    currentLobby: null,
    inviteState: {},
    inviteSlotAssignments: {},
    lobbyState: "standby",
    expandedSearch: false,
    queueStatus: null,
    matchmakingStarted: false,
  };
});

handlers.set(actions.LOBBY_KICK_PLAYER_FROM_LOBBY, (state, action) => {
  const currentLobby = state.currentLobby;
  if (!currentLobby) return state;
  const filteredMembers = (currentLobby.members || []).filter(m => m.userId !== action.playerToKickId);
  const updatedLobby = {
    ...currentLobby,
    members: filteredMembers,
    totalPlayers: Math.max(0, (currentLobby.totalPlayers || filteredMembers.length + 1) - 1),
    lastUpdatedAt: new Date().toISOString(),
  };
  const nextInviteState = { ...(state.inviteState || {}) };
  if (action.playerToKickId && nextInviteState[action.playerToKickId]) {
    delete nextInviteState[action.playerToKickId];
  }
  const updatedState = {
    ...state,
    currentLobby: updatedLobby,
    inviteState: nextInviteState,
  };
  return {
    ...updatedState,
    inviteSlotAssignments: computeInviteSlotAssignments(updatedState),
  };
});

handlers.set(actions.LOBBY_NOTIFY_PLAYER_HAS_BEEN_KICKED, (state, action) => {
  return {
    ...state,
    playerKickedNotification: {
      visible: true,
      message: action.message,
    },
  };
});

handlers.set(actions.LOBBY_HIDE_PLAYER_KICKED_NOTIFICATION, (state, action) => {
  return {
    ...state,
    playerKickedNotification: {
      visible: false,
      message: null,
    },
  };
});

handlers.set(actions.LOBBY_GET_LOBBY_BY_USER_ID, (state, action) => {
  const updatedState = {
    ...state,
    currentLobbyCode: action.lobby ? action.lobby.lobbyCode : null,   
    currentLobby: action.lobby,                 
  };
  return {
    ...updatedState,
    inviteSlotAssignments: computeInviteSlotAssignments(updatedState),
  };
});

handlers.set(actions.LOBBY_SHOW_PLAYER_JOINED_NOTIFICATION, (state, action) => {
  return {
    ...state,
    playerJoinedNotification: {
      visible: true,
      nickname: action.nickname,
      type: 'joined',
    },
  };
});

handlers.set(actions.LOBBY_HIDE_PLAYER_JOINED_NOTIFICATION, (state, action) => {
  return {
    ...state,
    playerJoinedNotification: {
      visible: false,
      nickname: null,
      type: null,
    },
  };
});

handlers.set(actions.LOBBY_SHOW_PLAYER_LEFT_NOTIFICATION, (state, action) => {
  return {
    ...state,
    playerJoinedNotification: {
      visible: true,
      nickname: action.nickname,
      type: 'left',
    },
  };
});

handlers.set(actions.LOBBY_HIDE_PLAYER_LEFT_NOTIFICATION, (state, action) => {
  return {
    ...state,
    playerJoinedNotification: {
      visible: false,
      nickname: null,
      type: null,
    },
  };
});

handlers.set(actions.LOBBY_CLEAR_INVITE_ERROR, (state, action) => {
  const updated = {
    ...state,
    inviteState: {
      ...state.inviteState,
      [action.guestId]: {
        invited: false,
        inviteError: null,
      },
    },
  };
  return {
    ...updated,
    inviteSlotAssignments: computeInviteSlotAssignments(updated),
  };
});

handlers.set(actions.LOBBY_FRIEND_SIDEBAR_SET_OPEN, (state, action) => {
  return {
    ...state,
    friendSidebarOpen: action.open,
  };
});

handlers.set(actions.LOBBY_FRIEND_SIDEBAR_SET_TAB, (state, action) => {
  return {
    ...state,
    friendSidebarActiveTab: action.tab,
  };
});

handlers.set(actions.LOBBY_SETTINGS_MENU_SET_OPEN, (state, action) => {
  return {
    ...state,
    lobbySettingsMenuOpen: action.open,
  };
});

handlers.set(actions.SHOW_UNVAILABLE_ACTION_MESSAGE, (state, action) => {
  return {
    ...state,
    showUnavailableActionMessage: true,
  };
});

handlers.set(actions.HIDE_UNVAILABLE_ACTION_MESSAGE, (state, action) => {
  return {
    ...state,
    showUnavailableActionMessage: false,
  };
});

handlers.set(actions.SHOW_QUEUE_ERROR_MESSAGE, (state, action) => {
  return {
    ...state,
    queueErrorMessage: action.message || "Failed to join matchmaking queue.",
  };
});

handlers.set(actions.HIDE_QUEUE_ERROR_MESSAGE, (state) => {
  return {
    ...state,
    queueErrorMessage: null,
  };
});

handlers.set(actions.SHOW_QUEUE_BLOCKED_MODAL, (state, action) => {
  return {
    ...state,
    queueBlockedModal: {
      open: true,
      reason: action.reason || null,
      message: action.message || "You cannot join matchmaking yet.",
      blockedMembers: Array.isArray(action.blockedMembers) ? action.blockedMembers : [],
    },
  };
});

handlers.set(actions.HIDE_QUEUE_BLOCKED_MODAL, (state) => {
  return {
    ...state,
    queueBlockedModal: {
      open: false,
      reason: null,
      message: null,
      blockedMembers: [],
    },
  };
});

handlers.set(actions.UPDATE_QUEUE_STATUS, (state, action) => {
  const prev = state.queueStatus || {};
  return {
    ...state,
    queueStatus: {
      playersInQueue: action.playersInQueue ?? prev.playersInQueue,
      playersNeeded: action.playersNeeded ?? prev.playersNeeded,
      algorithmEnabled: action.algorithmEnabled ?? prev.algorithmEnabled,
      hint: action.hint !== undefined ? action.hint : prev.hint,
      estimatedWaitSeconds: action.estimatedWaitSeconds !== undefined ? action.estimatedWaitSeconds : prev.estimatedWaitSeconds,
      geoQueue: action.geoQueue !== undefined ? action.geoQueue : prev.geoQueue,
      queueMode: action.queueMode !== undefined ? action.queueMode : prev.queueMode,
      smallerMatchOffer: action.smallerMatchOffer !== undefined ? action.smallerMatchOffer : prev.smallerMatchOffer,
    },
  };
});

handlers.set(actions.LOBBY_EXPAND_SEARCH, (state) => {
  return {
    ...state,
    expandedSearch: true,
  };
});

handlers.set(actions.LOBBY_SMALLER_MATCH_STATUS, (state, action) => {
  const prevQueueStatus = state.queueStatus || {};
  const prevOffer = prevQueueStatus.smallerMatchOffer;
  if (!prevOffer) return state;

  return {
    ...state,
    queueStatus: {
      ...prevQueueStatus,
      smallerMatchOffer: {
        ...prevOffer,
        status: action.status || prevOffer.status,
      },
    },
  };
});

handlers.set(actions.CLEAR_QUEUE_STATUS, (state) => {
  return {
    ...state,
    queueStatus: null,
  };
});

handlers.set(actions.SET_PLATFORM_STATS, (state, action) => ({
  ...state,
  platformStats: {
    serversOnline: action.serversOnline !== undefined ? action.serversOnline : (state.platformStats?.serversOnline ?? null),
    playersSearching: action.playersSearching !== undefined ? action.playersSearching : (state.platformStats?.playersSearching ?? null),
    activeMatches: action.activeMatches !== undefined ? action.activeMatches : (state.platformStats?.activeMatches ?? null),
  },
}));

handlers.set(actions.NOTIFY_MATCHMAKING_STARTED, (state, action) => {
  const nextUser =
    action.playerQueueActionStartTs != null
      ? objectAssign({}, state.user, {
          playerQueueActionStartTs: action.playerQueueActionStartTs,
        })
      : state.user;

  return objectAssign({}, state, {
    matchmakingStarted: true,
    lobbyState: "searching",
    user: nextUser,
  });
});

handlers.set(actions.RESET_MATCHMAKING_STARTED, (state) => {
  const nextUser = objectAssign({}, state.user);
  delete nextUser.playerQueueActionStartTs;

  return objectAssign({}, state, {
    matchmakingStarted: false,
    lobbyState: "standby",
    user: nextUser,
    expandedSearch: false,
  });
});

handlers.set(actions.LOBBY_CODE_NOT_FOUND, (state, action) => {
  return {
    ...state,
    lobbyCodeNotFound: action.lobbyCodeNotFound,
  };
});

handlers.set(actions.LOBBY_FULL, (state, action) => {
  return {
    ...state,
    lobbyFull: action.lobbyFull,
  };
});

handlers.set(actions.REPORT_PLAYER_UPDATE, (state, action) => {
  return objectAssign({}, state, {
    reportPlayer: {
      offenses: action.offenses,
      remarks: action.remarks
    }
  });
});

handlers.set(actions.REPORT_PLAYER_SUBMIT_SUCCESS, (state, action) => {
  return objectAssign({}, state, {
    reportPlayer: {
      offenses: {},
      remarks: ""
    }
  });
});

handlers.set(actions.REPORT_PLAYER_CLEAR, (state, action) => {
  return objectAssign({}, state, {
    reportPlayer: {
      offenses: {},
      remarks: ""
    }
  });
});

handlers.set(actions.CLOSE_MATCH_ACCEPT_DIALOG, (state, action) => {
  return objectAssign({}, state, {
    matchFound: null,
    matchAcceptDialogState: false,
    // Match start/close must dismiss a lingering vote; otherwise the modal survives redirect.
    mapVoteState: false,
  });
});

handlers.set(actions.CLEAR_MATCH_DATA, (state, action) => {
  return objectAssign({}, state, {
    matchFound: false,
    matchPageMissingId: null,
    matchData: null,
    usersMetaData: null,
    scoreboardData: null,
    matchAnalysis: null,
    // Keep a live map vote. Lobby remount (persistent vote bar → /lobby-v2)
    // calls clearMatchData to drop leftover scoreboard data, not the vote.
  });
});

handlers.set(actions.LEADERBOARD_LOAD_START, (state, action) => {
  const prevLb = state.leaderboard || {};
  return {
    ...state,
    leaderboard: {
      ...prevLb,
      loading: true,
      error: null,
      aggregates: action.preserveAggregates ? prevLb.aggregates ?? null : null,
      scope: action.scope || prevLb.scope || "global",
      region: "Europe",
      // keep existing rows until success; UI can show spinner for "load more"
    },
  };
});

handlers.set(actions.LEADERBOARD_LOAD_SUCCESS, (state, action) => {
  const payload = action.payload || {};
  const prevRows = (state.leaderboard && state.leaderboard.rows) || [];
  const combinedRows = action.append
    ? prevRows.concat(payload.rows || [])
    : (payload.rows || []);
  const seenRowKeys = new Set();
  const nextRows = combinedRows.filter((row) => {
    const key = row?._id ?? row?.userId ?? `rank:${row?.rank}`;
    const normalizedKey = String(key);
    if (seenRowKeys.has(normalizedKey)) return false;
    seenRowKeys.add(normalizedKey);
    return true;
  });

  return {
    ...state,
    leaderboard: {
      loading: false,
      error: null,
      scope: payload.scope || (state.leaderboard && state.leaderboard.scope) || "global",
      region: payload.region || "Europe",
      country: payload.country || null,
      viewerUserId: payload.viewerUserId || null,
      rows: nextRows,
      total: payload.total ?? nextRows.length,
      offset: payload.offset ?? 0,
      limit: payload.limit ?? (state.leaderboard && state.leaderboard.limit) ?? 50,
      aggregates:
        payload.aggregates ??
        (state.leaderboard && state.leaderboard.aggregates) ??
        null,
    },
  };
});

handlers.set(actions.LEADERBOARD_LOAD_ERROR, (state, action) => {
  return {
    ...state,
    leaderboard: {
      ...(state.leaderboard || {}),
      loading: false,
      error: action.error || "Failed to load leaderboard",
    },
  };
});

// ── Latest Matches Handlers ──

handlers.set(actions.LATEST_MATCHES_LOAD_START, (state) => {
  return {
    ...state,
    latestMatches: {
      ...(state.latestMatches || {}),
      status: "loading",
      error: null,
    },
  };
});

handlers.set(actions.LATEST_MATCHES_LOAD_SUCCESS, (state, action) => {
  return {
    ...state,
    latestMatches: {
      status: "success",
      matches: action.matches || [],
      serverTimeMs: action.serverTimeMs || Date.now(),
      error: null,
    },
  };
});

handlers.set(actions.LATEST_MATCHES_LOAD_ERROR, (state, action) => {
  return {
    ...state,
    latestMatches: {
      ...(state.latestMatches || {}),
      status: "error",
      error: action.error || "Failed to load latest matches",
    },
  };
});

// ── Membership Handlers ──

handlers.set(actions.SET_MEMBERSHIP_STATUS, (state, action) => {
  return {
    ...state,
    membership: {
      tier: action.membership.tier || "free",
      billingCycle: action.membership.billingCycle || null,
      subscriptionId: action.membership.subscriptionId || null,
      subscriptionStatus: action.membership.subscriptionStatus || null,
      cancelledAt: action.membership.cancelledAt || null,
      yearlyRenewalEligible: action.membership.yearlyRenewalEligible || false,
      expiresAt: action.membership.expiresAt || null,
      daysRemaining: action.membership.daysRemaining ?? null,
      grantedByAdmin: action.membership.grantedByAdmin || false,
      limits: action.membership.limits || null,
    },
  };
});

handlers.set(actions.SET_SELECTED_SERVER_LOCATIONS, (state, action) => {
  return {
    ...state,
    selectedServerLocations: action.locations,
  };
});

handlers.set(actions.SET_SERVER_LOCATIONS, (state, action) => {
  return {
    ...state,
    serverLocations: action.locations,
  };
});

handlers.set(actions.CS_SKILLS_LOADED, (state, action) => {
  return { ...state, csSkills: action.csSkills };
});

handlers.set(actions.CS_SKILLS_SAVED, (state, action) => {
  return { ...state, csSkills: action.csSkills };
});

handlers.set(actions.CS_SKILLS_TEMPLATE_LOADED, (state, action) => {
  return { ...state, csSkillsTemplate: action.template };
});

export default handlers;
